/**
 * The screen a message was marked read from.
 *
 * Sent with every mark-read and reply request so the read-history line can
 * say where each person read the message. The server stores exactly these
 * three keys and treats anything else as "not recorded", so a key added here
 * must also be added to CommLogReadEventWriter::SOURCES.
 *
 * - `inbox`: the Dashboard Inbox card, its pop-up list, and the Inbox page.
 * - `communication_center`: Archive, and replies sent in the Communication Center.
 * - `patient_chart`: replies sent from the patient's own comm log.
 */
export const READ_SOURCES = ['inbox', 'communication_center', 'patient_chart'] as const;

export type ReadSource = (typeof READ_SOURCES)[number];

export function isReadSource(value: unknown): value is ReadSource {
  return typeof value === 'string' && (READ_SOURCES as readonly string[]).includes(value);
}

/**
 * What the read line shows in brackets after the reader's name. Uses the
 * sidebar's own names for the two message screens, so the label matches what
 * staff click on.
 */
const READ_SOURCE_LABELS: Record<ReadSource, string> = {
  inbox: 'Inbox',
  communication_center: 'Communication Center',
  patient_chart: 'Patient Chart',
};

/**
 * The label for a read's screen, or null when there is none to show — no
 * screen recorded, or one this app does not know. Callers render nothing for
 * null: reads from before screens were saved keep their original line.
 */
export function readSourceLabel(value: unknown): string | null {
  return isReadSource(value) ? READ_SOURCE_LABELS[value] : null;
}
